import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, testSupabaseConnection } from '@/lib/supabase';
import { useAccount } from './AccountContext';
import { Transaction } from '@/types/transaction';

interface TransactionContextType {
  transactions: Transaction[];
  loading: boolean;
  error: string | null;
  refreshTransactions: () => Promise<void>;
}

const TransactionContext = createContext<TransactionContextType | undefined>(undefined);

export const TransactionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { accountType } = useAccount();

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Test Supabase connection first
      const isConnected = await testSupabaseConnection();
      if (!isConnected) {
        setError('Unable to connect to the database. Please check your internet connection.');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('Please sign in to view your transactions');
        return;
      }

      console.log('Loading transactions for user:', user.id, 'account type:', accountType);

      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .eq('account_type', accountType)
        .order('transaction_date', { ascending: false });

      if (error) {
        console.error('Transactions error:', error);
        setError(`Failed to load transactions: ${error.message}`);
        return;
      }

      console.log('Loaded transactions:', data?.length || 0);
      setTransactions(data || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Subscribe to real-time changes
  useEffect(() => {
    const subscription = supabase
      .channel('transactions_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
        },
        (payload) => {
          console.log('Transaction change received:', payload);
          loadTransactions();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Initial load
  useEffect(() => {
    loadTransactions();
  }, [accountType]);

  return (
    <TransactionContext.Provider 
      value={{ 
        transactions, 
        loading, 
        error, 
        refreshTransactions: loadTransactions 
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
};

export const useTransactions = () => {
  const context = useContext(TransactionContext);
  if (context === undefined) {
    throw new Error('useTransactions must be used within a TransactionProvider');
  }
  return context;
}; 